/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */

// Lib dependencies
import {
	queryOptions,
	useQuery,
} from '@tanstack/react-query';

// Types
import { TApiResponseGetPosts } from '@global-types/api/api';
import { TParamsGetApi } from '@global-types/index';

// Lib
import { apiClient } from '@lib/api-client';
import { QueryConfig } from '@lib/react-query';

// Constants
import { ACLS } from '@config/constants/acls';
import { queryKeys } from '@config/constants/query-keys';
import { useAuthorization } from '@lib/authorization';

/* -------------------------------------------------------------------------- */
/*                                     Api                                    */
/* -------------------------------------------------------------------------- */
export const getPosts = (
	params: Partial<TParamsGetApi>
): Promise<TApiResponseGetPosts> => {
	return apiClient.get('/posts', {
		params,
	});
};

/* -------------------------------------------------------------------------- */
/*                                    Type                                    */
/* -------------------------------------------------------------------------- */
type TUseGetPostsOptions = {
	params: Partial<TParamsGetApi>;
	queryConfig?: QueryConfig<typeof getPosts>;
};

/* -------------------------------------------------------------------------- */
/*                                    Helper                                  */
/* -------------------------------------------------------------------------- */
export const getPostsQueryOptions = (
	params: Partial<TParamsGetApi> = {}
) => {
	return queryOptions({
		queryKey: params
			? [
					queryKeys.app.dashboard.posts.list.key,
					{ params },
				]
			: [queryKeys.app.dashboard.posts.list.key],
		queryFn: () => getPosts(params),
	});
};

/* -------------------------------------------------------------------------- */
/*                                     Hook                                   */
/* -------------------------------------------------------------------------- */
export const useGetPosts = ({
	params,
	queryConfig,
}: TUseGetPostsOptions) => {
	const { checkAcls } = useAuthorization();

	return useQuery({
		...getPostsQueryOptions(params),
		...queryConfig,
		enabled: checkAcls([ACLS.POSTS.LIST]),
	});
};
